/**
 * RFIs API Client
 *
 * GW-3056: Client for gw-services-api RFI lifecycle endpoints.
 * Base URL: services.{env}.generalwisdom.com
 * All requests include Accept-Version: 1.0 header via ServicesApiClient.
 */

import { assertOrgId } from '@/types/orgId';
import type { RFI, RFIListResponse, RFIStatus, SubmitRFIRequest } from '@/types/rfi.types';
// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.

import { ServicesApiClient } from './servicesApiClient';

class RFIsAPI extends ServicesApiClient {
  /**
   * Submit a new RFI as a buyer.
   * POST /rfis
   */
  async submitRFI(data: SubmitRFIRequest): Promise<RFI> {
    return this.post<RFI>('/rfis', data);
  }

  /**
   * List RFIs for the authenticated buyer.
   * GET /personal/rfis
   */
  async listBuyerRFIs(params?: {
    limit?: number;
    cursor?: string;
    status?: RFIStatus;
  }): Promise<RFIListResponse> {
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<RFIListResponse>(`/personal/rfis${qs}`);
  }

  /**
   * List RFIs for an organization (org admin view).
   * GET /organizations/:orgId/rfis
   */
  async listOrgRFIs(
    orgId: string,
    params?: { limit?: number; cursor?: string; status?: RFIStatus }
  ): Promise<RFIListResponse> {
    assertOrgId(orgId);
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<RFIListResponse>(`/organizations/${orgId}/rfis${qs}`);
  }

  /**
   * List RFIs assigned to the authenticated consultant.
   * GET /consultant/rfis
   */
  async listConsultantRFIs(params?: {
    limit?: number;
    cursor?: string;
    status?: RFIStatus;
  }): Promise<RFIListResponse> {
    const qs = params ? this.buildQueryString(params) : '';
    return this.get<RFIListResponse>(`/consultant/rfis${qs}`);
  }

  /**
   * Get a single RFI by ID.
   * GET /rfis/:rfiId
   */
  async getRFI(rfiId: string): Promise<RFI> {
    return this.get<RFI>(`/rfis/${rfiId}`);
  }

  /**
   * Assign a consultant to an RFI.
   * PUT /rfis/:rfiId/assign
   */
  async assignConsultant(rfiId: string, consultantUserId: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/assign`, { consultantUserId });
  }

  /**
   * Cancel an RFI.
   * PUT /rfis/:rfiId/cancel
   */
  async cancelRFI(rfiId: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/cancel`, {});
  }

  /**
   * Mark an RFI as delivered with a deliverable reference.
   * PUT /rfis/:rfiId/deliver
   */
  async deliverRFI(rfiId: string, deliverableReference: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/deliver`, { deliverableReference });
  }

  /**
   * Accept the deliverable and complete an RFI.
   * PUT /rfis/:rfiId/accept
   */
  async acceptRFI(rfiId: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/accept`, {});
  }

  /**
   * Raise a dispute on an RFI.
   * PUT /rfis/:rfiId/dispute
   */
  async disputeRFI(rfiId: string, reason: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/dispute`, { reason });
  }

  /**
   * Resolve a dispute on an RFI (admin only).
   * PUT /rfis/:rfiId/resolve-dispute
   */
  async resolveDispute(
    rfiId: string,
    resolution: 'completed' | 'refunded',
    notes?: string
  ): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/resolve-dispute`, { resolution, notes });
  }

  /**
   * Propose an LTIOV extension for an RFI.
   * PUT /rfis/:rfiId/extend-ltiov
   */
  async extendLTIOV(rfiId: string, proposedLtiov: string, reason: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/extend-ltiov`, { proposedLtiov, reason });
  }

  /**
   * Approve a proposed LTIOV extension.
   * PUT /rfis/:rfiId/approve-extension
   */
  async approveExtension(rfiId: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/approve-extension`, {});
  }

  /**
   * Reject a proposed LTIOV extension.
   * PUT /rfis/:rfiId/reject-extension
   */
  async rejectExtension(rfiId: string): Promise<RFI> {
    return this.put<RFI>(`/rfis/${rfiId}/reject-extension`, {});
  }
}

export const rfisApi = new RFIsAPI();
